import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Icon } from '@iconify/react';
import WarningPage from './WarningPage';
import RetryNotice, { RetryInline } from './RetryNotice';
import { UpdateButton } from '../../components/mui/Buttons/Buttons';
import apiRequest from '../../api/request';
import useWarningMonth from './useWarningMonth';
import WorkingMonthBar from './WorkingMonthBar';
import EffectiveFromField from './EffectiveFromField';
import AppliesCell from './AppliesCell';
import ActiveSwitch from './ActiveSwitch';
import { defaultEffectiveMonth, emptyEffective, startsWithin } from './effectiveWindow';
import { RULES, numberField, validateNumber } from './thresholdValidation';
import MetricDefinitionNote from './MetricDefinitionNote';

const OPERATORS = [
  { value: '<', label: 'is below' },
  { value: '<=', label: 'is at or below' },
  { value: '=', label: 'equals' },
  { value: '>=', label: 'is at or above' },
  { value: '>', label: 'is above' },
];

const emptyForm = { wing_id: '', comparison_operator: '<', threshold_value: '', is_active: true };

// Revenue Per OGA's config screen - OGD Team model (Requirement Document
// v1.0 S3/S4.7). A "team" here is one of OGD's 5 selling squads (Ventas
// Squad, Goal Setters, Deal Maestros, Chaser Crew, ECPC) - a different
// team concept from Sales Count's CRM reporting-chain teams. One
// threshold per squad, not a range-based slab. Add/edit is an inline
// panel on the page, not a popup.
const OgdTeamConfig = () => {
  const [month, setMonth] = useWarningMonth(); // the month being worked on - passed on to the results screens
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [rowsError, setRowsError] = useState(null);
  const [teams, setTeams] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // a blank threshold only complains after the first Save attempt
  const [submitted, setSubmitted] = useState(false);
  const [teamsError, setTeamsError] = useState(null);

  const fetchSeq = useRef(0); // each load gets a number; only the newest may update the screen (fast clicks must not show an older answer)
  const fetchRows = ({ silent = false } = {}) => {
    const seq = ++fetchSeq.current;
    if (!silent) setRowsLoading(true); // a silent reload (after a switch) keeps the table on screen - no flash
    // apiRequest resolves even on a failed call (src/api/request.js) - this status check is required, a
    // .catch alone would never fire and a server error would silently render as "no thresholds configured".
    return apiRequest({ url: `warning-dashboard/ogd-team-thresholds?month=${month.format('YYYY-MM-DD')}`, method: 'get' })
      .then((res) => {
        if (seq !== fetchSeq.current) return; // a newer load replaced this one
        if (res?.status !== 'success') {
          setRowsError(res?.message || 'Could not load OGD team thresholds');
          return;
        }
        setRows(res?.data?.rows || []);
        setRowsError(null);
      })
      .catch((err) => {
        if (seq === fetchSeq.current) setRowsError(err?.response?.data?.message || 'Could not load OGD team thresholds');
      })
      .finally(() => {
        if (seq === fetchSeq.current) setRowsLoading(false);
      });
  };

  // The squad dropdown's own load - a failure used to leave the list silently empty. Retry reloads only this list.
  const loadTeams = () => {
    setTeamsError(null);
    return apiRequest({ url: 'warning-dashboard/ogd-teams', method: 'get' })
      .then((res) => {
        if (res?.status !== 'success') {
          setTeamsError(res?.message || 'Could not load the squads');
          return;
        }
        setTeams(res?.data?.rows || []);
      })
      .catch((err) => setTeamsError(err?.response?.data?.message || 'Could not load the squads'));
  };

  useEffect(() => {
    fetchRows();
  }, [month]); // reload when the working month changes

  useEffect(() => {
    loadTeams();
  }, []);

  const teamName = (wingId) => teams.find((t) => t.wing_id === Number(wingId))?.wing_name || `Squad #${wingId}`;

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, ...emptyEffective() }); // the month to apply from is read fresh each time the form opens
    setSaveError(null);
    setSubmitted(false);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      wing_id: row.wing_id,
      comparison_operator: row.comparison_operator,
      threshold_value: row.threshold_value,
      is_active: true, // opening an edit on a switched-off threshold means "use it" - switch it off again below if not
      ...emptyEffective(),
    });
    setSaveError(null);
    setSubmitted(false);
    setFormOpen(true);
  };

  const closeForm = () => setFormOpen(false);

  // BUG-03: never blank, never negative; and one current threshold per squad (the server enforces both too)
  const thresholdField = numberField(form.threshold_value, 'Threshold', RULES.nonNegative14, submitted, { helper: false });
  // Several versions of one threshold are normal now (a change from a later month, a one-month override), so only a
  // second ACTIVE OPEN-ENDED row starting in the chosen month or later is a conflict - one that starts earlier is simply closed when the new one begins. Edits and one-month overrides are checked by the server, which knows the months.
  const duplicateError =
    form.wing_id && !editingId && rows.find((r) => r.is_active && startsWithin(r, form) && String(r.wing_id) === String(form.wing_id))
      ? `${teamName(form.wing_id)} already has a threshold that starts in the chosen month or later - edit it, or choose a later month`
      : '';
  const keyError = submitted && !form.wing_id ? 'Choose a squad' : '';

  const handleSave = () => {
    setSubmitted(true);
    if (!form.wing_id || duplicateError || validateNumber(form.threshold_value, 'Threshold', RULES.nonNegative14)) return;
    setSaving(true);
    const request = editingId
      ? apiRequest({ url: `warning-dashboard/ogd-team-thresholds/${editingId}`, method: 'put', data: form })
      : apiRequest({ url: 'warning-dashboard/ogd-team-thresholds', method: 'post', data: form });

    request
      .then((res) => {
        if (res?.status !== 'success') {
          setSaveError(res?.message || 'Could not save this threshold');
          return;
        }
        setFormOpen(false);
        return fetchRows();
      })
      .catch((err) => setSaveError(err?.response?.data?.message || 'Could not save this threshold'))
      .finally(() => setSaving(false));
  };

  const [toggling, setToggling] = useState({}); // rows whose switch request is still in flight - one at a time per row
  const handleToggleActive = (row) => {
    if (toggling[row.id]) return;
    setToggling((t) => ({ ...t, [row.id]: true }));
    const nextActive = !row.is_active;
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, is_active: nextActive } : r)));
    apiRequest({ url: `warning-dashboard/ogd-team-thresholds/${row.id}/active`, method: 'patch', data: { is_active: nextActive, effective_month: defaultEffectiveMonth() } })
      .then((res) => {
        if (res?.status !== 'success') {
          setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, is_active: row.is_active } : r)));
        } else {
          fetchRows({ silent: true }); // a toggle can start a new version from this month
        }
      })
      .catch(() => {
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, is_active: row.is_active } : r)));
      })
      .finally(() => setToggling((t) => { const next = { ...t }; delete next[row.id]; return next; }));
  };

  const operatorLabel = (op) => OPERATORS.find((o) => o.value === op)?.label || op;

  return (
    <WarningPage
      title="Revenue Per OGA - Team Thresholds"
      actions={
        <Box display="flex" gap={1}>
          <Button component={Link} to={`/dashboard/warning-dashboard/revenue-per-oga?month=${month.format('YYYY-MM-DD')}`} variant="outlined">
            View Results
          </Button>
          {!formOpen && (
            <Button startIcon={<Icon icon="mynaui:config-solid" width="20" />} variant="contained" onClick={openCreate}>
              Add Team Threshold
            </Button>
          )}
        </Box>
      }
    >
      <WorkingMonthBar month={month} setMonth={setMonth} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Average Team Target Per OGA = sum of each active squad member's
        target, divided by the squad's active headcount - members with no
        target still count in the headcount, pulling the average down.
      </Typography>

      {formOpen && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2, borderColor: 'primary.main' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            {editingId ? 'Edit Team Threshold' : 'Add Team Threshold'}
          </Typography>

          <Box display="flex" flexDirection="column" gap={2}>
            <MetricDefinitionNote metricKey="revenue_per_oga" />
            <TextField
              select
              label="Squad"
              value={form.wing_id}
              onChange={(e) => setForm({ ...form, wing_id: e.target.value })}
              error={!!(keyError || duplicateError || teamsError)}
              helperText={teamsError ? <RetryInline message={teamsError} onRetry={loadTeams} /> : keyError || duplicateError || undefined}
              sx={{ maxWidth: 320 }}
            >
              {teams.map((t) => (
                <MenuItem key={t.wing_id} value={t.wing_id}>{t.wing_name}</MenuItem>
              ))}
            </TextField>

            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography>Warns when average target</Typography>
              <TextField
                select
                size="small"
                value={form.comparison_operator}
                onChange={(e) => setForm({ ...form, comparison_operator: e.target.value })}
                sx={{ minWidth: 160 }}
              >
                {OPERATORS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                value={form.threshold_value}
                onChange={(e) => setForm({ ...form, threshold_value: e.target.value })}
                sx={{ width: 140 }}
                {...thresholdField.props}
              />
            </Box>

            <EffectiveFromField form={form} setForm={setForm} />

            <ActiveSwitch form={form} setForm={setForm} offHint="While off, no warning is raised for this squad." />

            {thresholdField.error && <Typography color="error.main" variant="body2">{thresholdField.error}</Typography>}
            {saveError && <Typography color="error.main" variant="body2">{saveError}</Typography>}

            <Box display="flex" gap={1}>
              <UpdateButton action={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</UpdateButton>
              <Button variant="text" onClick={closeForm} disabled={saving}>Cancel</Button>
            </Box>
          </Box>
        </Paper>
      )}

      {rowsLoading && (
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
          <CircularProgress size={18} />
          <Typography color="text.secondary">Loading thresholds...</Typography>
        </Box>
      )}

      {!rowsLoading && rowsError && <RetryNotice message={rowsError} onRetry={fetchRows} />}

      {!rowsLoading && !rowsError && rows.length === 0 && (
        <Typography color="text.secondary">No team thresholds configured yet.</Typography>
      )}

      {!rowsLoading && !rowsError && rows.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#FAF8F3' }}>
                <TableCell><strong>Squad</strong></TableCell>
                <TableCell><strong>Warns when average target</strong></TableCell>
                <TableCell><strong>Applies</strong></TableCell>
                <TableCell><strong>Active</strong></TableCell>
                <TableCell><strong>Action</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{teamName(row.wing_id)}</TableCell>
                  <TableCell>{operatorLabel(row.comparison_operator)} ₹{Number(row.threshold_value).toLocaleString()}</TableCell>
                  <TableCell><AppliesCell row={row} month={month} /></TableCell>
                  <TableCell>
                    <Switch checked={!!row.is_active} disabled={!!toggling[row.id]} onChange={() => handleToggleActive(row)} />
                  </TableCell>
                  <TableCell>
                    <UpdateButton size="small" action={() => openEdit(row)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </WarningPage>
  );
};

export default OgdTeamConfig;
